// PushBoard.tsx
// Router component that renders Campaign or Automatic board

import React from "react";
import { CampaignBoard } from "~/components/pages/CampaignBoard";
import { AutomaticBoard } from "~/components/pages/AutomaticBoard";
import { trpc } from "~/api/trpc";

interface PushBoardProps {
  type: "campaign" | "automatic";
}

export function PushBoard({ type }: PushBoardProps) {
  // Connect to TRPC mutations
  const sendTestPushNotificationMutation = trpc.pushNotifications.sendTestPushNotification.useMutation();
  const sendPushNotificationBatchMutation = trpc.pushNotifications.sendPushNotificationBatch.useMutation();
  const upsertTransactionalPushNotificationConfigMutation = trpc.pushNotifications.upsertTransactionalPushNotificationConfig.useMutation();

  const handleSendTestPushnotification = async (input: {
    email: string;
    notification: {
      data?: Record<string, unknown> | undefined;
      title?: string | undefined;
      subtitle?: string | undefined;
      body?: string | undefined;
      sound?:
        | string
        | {
            critical?: boolean | undefined;
            name?: string | null | undefined;
            volume?: number | undefined;
          }
        | null
        | undefined;
      ttl?: number | undefined;
      expiration?: number | undefined;
      priority?: "default" | "normal" | "high" | undefined;
      interruptionLevel?: "active" | "critical" | "passive" | "time-sensitive" | undefined;
      badge?: number | undefined;
      channelId?: string | undefined;
      icon?: string | undefined;
      richContent?: { image?: string | undefined } | undefined;
      categoryId?: string | undefined;
      mutableContent?: boolean | undefined;
      _contentAvailable?: boolean | undefined;
    };
    campaignId: string;
    mergeVariables?: Record<string, string> | undefined;
  }) => {
    return sendTestPushNotificationMutation.mutateAsync(input);
  };

  const handleSendPushNotificationBatch = async (input: {
    recipients: Array<{
      email: string;
      mergeVariables?: Record<string, string>;
    }>;
    templateId: string;
    campaignId?: string | undefined;
    onProgress?: (current: number, total: number, batchResults: any[]) => void;
  }) => {
    const { recipients, templateId, campaignId, onProgress } = input;
    
    // Implement batching logic (100 recipients per batch)
    const BATCH_SIZE = 100;
    const batches = [];
    for (let i = 0; i < recipients.length; i += BATCH_SIZE) {
      batches.push(recipients.slice(i, i + BATCH_SIZE));
    }
    
    const allResults: any[] = [];
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];

      try {
        const result = await sendPushNotificationBatchMutation.mutateAsync({
          recipients: batch,
          templateId,
          campaignId,
        });

        allResults.push(result);
      } catch (e: any) {
        // Isolate the failure: count this batch as failed and keep going so a
        // single rejected batch never discards earlier successful batches.
        allResults.push({
          results: {
            total: batch.length,
            successful: 0,
            failed: batch.length,
            errors: batch.map((r) => ({
              email: r.email,
              error: e?.message ?? "Batch rejected",
            })),
          },
        });
      }

      if (onProgress) {
        onProgress(
          Math.min((i + 1) * BATCH_SIZE, recipients.length),
          recipients.length,
          allResults
        );
      }
    }
    
    const totalSuccessful = allResults.reduce(
      (sum, r) => sum + (r.results?.successful || 0),
      0
    );
    const totalFailed = allResults.reduce(
      (sum, r) => sum + (r.results?.failed || 0),
      0
    );
    const allErrors = allResults.flatMap((r) => r.results?.errors || []);
    
    return {
      success: totalSuccessful > 0,
      results: {
        total: recipients.length,
        successful: totalSuccessful,
        failed: totalFailed,
        errors: allErrors,
      },
      message: `Push notifications sent to ${totalSuccessful} out of ${recipients.length} recipients`,
    };
  };

  const handleUpsertTransactionalConfig = async (input: {
    type: any; // TransactionalPushNotificationType
    enabled?: boolean;
    templateId?: string | null;
  }) => {
    return upsertTransactionalPushNotificationConfigMutation.mutateAsync(input);
  };

  if (type === "campaign") {
    return (
      <CampaignBoard
        handleSendTestPushnotification={handleSendTestPushnotification}
        handleSendPushNotificationBatch={handleSendPushNotificationBatch}
      />
    );
  }

  if (type === "automatic") {
    return (
      <AutomaticBoard
        handleUpsertTransactionalConfig={handleUpsertTransactionalConfig}
      />
    );
  }

  return null;
}